/**
 * Performance Stylesheet
 *
 * Maps node types to visualiser names.
 * Resolution: stylesheet override > base mapping > null.
 *
 * Stylesheets are reusable across projects.
 */

import { getBaseMapping } from './VisualiserRegistry';

// ============ Types ============

export interface PerformanceStylesheet {
  name: string;
  mappings: Record<string, string>;  // nodeType -> visualiserName
}

// ============ Default stylesheet (empty — falls back to base mappings) ============

export function createDefaultStylesheet(): PerformanceStylesheet {
  return {
    name: 'default',
    mappings: {},
  };
}

// ============ Resolution ============

export interface ResolvedMapping {
  visualiserName: string;
  params: Record<string, string>;
}

/**
 * Resolve the visualiser mapping for a node type.
 * Stylesheet override > base mapping > mesh fallback (for nodes with three:group output) > null.
 * Returns visualiser name + param mappings (visualiser prop -> endpoint id).
 */
export function resolveVisualiser(
  nodeType: string,
  stylesheet: PerformanceStylesheet,
  endpoints?: Record<string, { dataType?: string; isOutput?: boolean }>,
): ResolvedMapping | null {
  // Stylesheet override (no param mappings — just the name)
  const stylesheetName = stylesheet.mappings[nodeType];
  if (stylesheetName) {
    return { visualiserName: stylesheetName, params: {} };
  }

  // Base mapping from plugin (includes param mappings)
  const base = getBaseMapping(nodeType);
  if (base) {
    return { visualiserName: base.visualiserName, params: base.params };
  }

  // Fallback: nodes with a three:group output use mesh visualiser
  if (endpoints) {
    for (const ep of Object.values(endpoints)) {
      if (ep.dataType === 'three:group' && ep.isOutput) {
        return { visualiserName: 'mesh', params: {} };
      }
    }
  }

  return null;
}
